'use client';

import { useCallback, useEffect, useState } from 'react';
import { useStandaloneDisplayMode } from './useStandaloneDisplayMode';

const DISMISS_KEY = 'ertzyx.pwaInstall.dismissedAt';
const DISMISS_COOLDOWN_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

function isWithinDismissCooldown(): boolean {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const dismissedAt = Number(raw);
    if (!Number.isFinite(dismissedAt)) return false;
    return Date.now() - dismissedAt < DISMISS_COOLDOWN_MS;
  } catch {
    return false;
  }
}

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export function detectIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  const isIOSDevice = /iPad|iPhone|iPod/.test(ua);
  // iPadOS 13+ reports as "Macintosh" but exposes multitouch, unlike real macOS.
  const isIPadOS = ua.includes('Macintosh') && navigator.maxTouchPoints > 1;
  return isIOSDevice || isIPadOS;
}

export function detectSafari(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  return /Safari/.test(ua) && !/Chrome|CriOS|FxiOS|EdgiOS/.test(ua);
}

/**
 * Ertzyx install-prompt state. Never prompts automatically -- callers decide
 * where/when to surface `canInstall`/`isIOS` (Settings, account menu, a
 * contextual banner after meaningful use). Dismissal is tracked here so a
 * banner-style caller can hide itself for a while without nagging; a
 * deliberately-visited surface like a Settings page should ignore
 * `dismissed` and just check `isStandalone`.
 */
export function usePwaInstall() {
  const isStandalone = useStandaloneDisplayMode();
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isSafari, setIsSafari] = useState(false);
  const [dismissed, setDismissed] = useState(true); // default true until localStorage checked, avoids a flash

  useEffect(() => {
    setIsIOS(detectIOS());
    setIsSafari(detectSafari());
    setDismissed(isWithinDismissCooldown());

    const onBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      setDeferredPrompt(event as BeforeInstallPromptEvent);
    };
    const onInstalled = () => setDeferredPrompt(null);

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return 'unavailable' as const;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    return outcome;
  }, [deferredPrompt]);

  const dismiss = useCallback(() => {
    setDismissed(true);
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      // ignore storage errors (private browsing, quota)
    }
  }, []);

  return {
    isStandalone,
    canInstall: deferredPrompt !== null,
    isIOS,
    isIOSSafari: isIOS && isSafari,
    dismissed,
    promptInstall,
    dismiss,
  };
}
